import { useState, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  updatePatientAction,
  addMarriageInfoAction,
  getPatientlistbygenderAction,
  getPatientsAction,
  addFamilyTreeAction,
  editFamilyTreeAction,
  deleteFamilyTreeAction,
  fetchFamilyTreeListAction,
} from "../../../../../store/actions/PatientsAction"
import { getDiseasesAction } from "../../../../../store/actions/DiseasesAction"
import Select from "react-select"
import Swal from "sweetalert2"
import { handleSuccess, showErrorAlert } from "../../../../../services/ErrorHandlingService"

// Helper function to format patient name from first, middle, last names
const formatPatientName = (patient) => {
  if (!patient) return "Select Patient"
  
  const firstName = patient.first_name || patient.patient_name?.split(" ")[0] || ""
  const middleName = patient.middile_name || patient.middle_name || ""
  const lastName = patient.last_name || (patient.patient_name?.split(" ").slice(1).join(" ")) || ""
  
  const nameParts = [firstName, middleName, lastName].filter(part => part && part.trim())
  return nameParts.length > 0 ? nameParts.join(" ") : "Unnamed Patient"
}

// Helper function to format patient from API response
const formatPatientFromResponse = (patient) => {
  if (!patient) return null
  return {
    value: patient.patient_id || patient.id,
    label: formatPatientName(patient),
    first_name: patient.first_name,
    middile_name: patient.middile_name,
    last_name: patient.last_name,
  }
}

const formatPatientsForDropdown = (patients, currentPatientId = null, existingFamilyMemberIds = []) => {
  if (!Array.isArray(patients)) return []
  return patients
    .filter((patient) => {
      if (currentPatientId && patient.patient_id === currentPatientId) {
        return false
      }
      if (existingFamilyMemberIds.includes(patient.patient_id)) {
        return false
      }
      return true
    })
    .map((patient) => ({
      value: patient.patient_id,
      label: formatPatientName(patient),
      first_name: patient.first_name,
      middile_name: patient.middile_name,
      last_name: patient.last_name,
    }))
}

const occupationOptions = [
  { value: "Doctor", label: "Doctor" },
  { value: "Nurse", label: "Nurse" },
  { value: "Engineer", label: "Engineer" },
  { value: "Teacher", label: "Teacher" },
  { value: "Farmer", label: "Farmer" },
  { value: "Housewife", label: "Housewife" },
  { value: "Student", label: "Student" },
  { value: "Businessman", label: "Businessman" },
  { value: "Driver", label: "Driver" },
  { value: "Electrician", label: "Electrician" },
  { value: "Plumber", label: "Plumber" },
  { value: "Carpenter", label: "Carpenter" },
  { value: "Lawyer", label: "Lawyer" },
  { value: "Police Officer", label: "Police Officer" },
  { value: "Artist", label: "Artist" },
  { value: "Actor", label: "Actor" },
  { value: "Scientist", label: "Scientist" },
  { value: "Retired", label: "Retired" },
  { value: "Unemployed", label: "Unemployed" },
  { value: "Other", label: "Other" },
]

const relationOptions = [
  { label: "Father", value: "Father" },
  { label: "Mother", value: "Mother" },
  { label: "Brother", value: "Brother" },
  { label: "Sister", value: "Sister" },
  { label: "Son", value: "Son" },
  { label: "Daughter", value: "Daughter" },
  { label: "Husband", value: "Husband" },
  { label: "Wife", value: "Wife" },
  { label: "Grandfather", value: "Grandfather" },
  { label: "Grandmother", value: "Grandmother" },
  { label: "Uncle", value: "Uncle" },
  { label: "Aunt", value: "Aunt" },
  { label: "Cousin", value: "Cousin" },
  { label: "Guardian", value: "Guardian" },
  { label: "Friend", value: "Friend" },
]

const intensityOptions = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
  { value: "Very High", label: "Very High" },
]

const PersonalInfoModal = ({ isOpen, onClose, formData, onSubmit, loading }) => {
  const dispatch = useDispatch()
  const [localFormData, setLocalFormData] = useState({
    patient_photo: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    gender: "",
    dob: "",
    weight: "",
    occupation: "",
    maritalStatus: "No",
    marriageEntries: [],
    disease_ids: [],
    disease_status: "",
    current_disease_id: "",
    familyMembers: [],
    referred_by_patient_id: "",
    referred_by_manual: "",
    disease_percentage: "",
    intensity_of_treatment: "",
    spouse_name: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [selectedIntensity, setSelectedIntensity] = useState(null)

  // Family Tree States
  const [familyMembers, setFamilyMembers] = useState([])
  const [editingFamilyId, setEditingFamilyId] = useState(null)
  const [familyFormData, setFamilyFormData] = useState({
    relation_name: null,
    family_patient_id: null,
  })
  const [isProcessingFamily, setIsProcessingFamily] = useState(false)

  const patients = useSelector((state) => state.patients.patients || [])
  const diseases = useSelector((state) => state.diseases.diseases || [])
  const familyTreeList = useSelector((state) => state.patients?.familyTreeList || [])

  const patientOptions = useMemo(() => {
    const existingFamilyMemberIds = familyMembers.map((member) => member.family_patient_id).filter(Boolean)
    return formatPatientsForDropdown(patients, formData.patient_id, existingFamilyMemberIds)
  }, [patients, formData.patient_id, familyMembers])

  // Helper function to fix malformed photo URLs
  const getValidPhotoUrl = (photoUrl) => {
    if (!photoUrl) return ""
    if ((photoUrl.match(/https:\/\//g) || []).length > 1) {
      const parts = photoUrl.split("https://")
      return "https://" + parts[parts.length - 1]
    }
    return photoUrl
  }

  // Load diseases and patients when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(getDiseasesAction())
      dispatch(getPatientsAction(false))
    }
  }, [isOpen, dispatch])

  useEffect(() => {
    if (formData.patient_id) {
      dispatch(fetchFamilyTreeListAction(formData.patient_id))
    }
  }, [formData.patient_id, dispatch])

  useEffect(() => {
    // Transform family tree list to include formatted patient name
    const transformedFamilyMembers = familyTreeList.map((member) => ({
      ...member,
      formattedName: formatPatientName({
        first_name: member.first_name,
        middile_name: member.middile_name,
        last_name: member.last_name,
        patient_name: member.patient_name,
      }),
      // Store the complete patient object for editing
      patientData: {
        patient_id: member.family_patient_id,
        first_name: member.first_name,
        middile_name: member.middile_name,
        last_name: member.last_name,
      }
    }))
    setFamilyMembers(transformedFamilyMembers)
  }, [familyTreeList])

  // Set form data when modal opens or formData changes
  useEffect(() => {
    if (isOpen && formData && diseases.length > 0) {
      let marriageEntries = []
      let maritalStatus = "No"
      if (formData.marriageInfo && Array.isArray(formData.marriageInfo) && formData.marriageInfo.length > 0) {
        maritalStatus = "Yes"
        marriageEntries = formData.marriageInfo.map((info, index) => ({
          marriageHistory: info.marriage_history || `${index + 1}${getOrdinalSuffix(index + 1)}`,
          marriageDate: info.date_of_marriage || "",
          spouseOption: info.spouse_option === "Without Spouse" ? "withoutSpouse" : "withSpouse",
          spouseName: info.husband_or_spouse_name || "",
          spouseDetails: info.spouse_details || null,
          spouseMobile: info.spouse_mobile || "",
          spouseEmail: info.spouse_email || "",
          spouseDOB: info.spouse_dob || "",
          spouseGender: info.spouse_gender || "",
          spouseOccupation: info.spouse_occupation || "",
        }))
      } else {
        marriageEntries = [
          {
            marriageHistory: "",
            marriageDate: "",
            spouseOption: "",
            spouseDetails: null,
            spouseName: "",
            spouseMobile: "",
            spouseEmail: "",
            spouseDOB: "",
            spouseGender: "",
            spouseOccupation: "",
          },
        ]
      }

      let diseaseIds = []
      if (formData.diseases && Array.isArray(formData.diseases)) {
        diseaseIds = formData.diseases.map((disease) => String(disease.disease_id))
      } else if (formData.disease_names) {
        if (typeof formData.disease_names === "string") {
          diseaseIds = formData.disease_names.split("|").filter((id) => id !== "" && id !== "0")
        } else if (Array.isArray(formData.disease_names)) {
          diseaseIds = formData.disease_names.filter((id) => id !== "" && id !== "0")
        }
      }

      const currentDiseaseObj = formData.diseases?.find(
        (d) => String(d.disease_id) === String(formData.current_disease_id || "")
      )
      const diseaseStatus = formData.diseaseStatus || ""
      const initialCurrentDiseaseId = currentDiseaseObj?.disease_id || formData.current_disease_id || ""

      diseaseIds = diseaseIds.map((id) => String(id))

      let initialIntensity = intensityOptions.find(
        (opt) => opt.value.toLowerCase() === String(formData.intensity_of_treatment || "").toLowerCase()
      ) || null

      const initialDiseasePercentage = formData.disease_percentage || ""
      const initialReferredBy = formData.referred_by || ""

      let fixedPhotoUrl = formData.profileImage1 || ""
      if (fixedPhotoUrl && (fixedPhotoUrl.match(/https:\/\//g) || []).length > 1) {
        const parts = fixedPhotoUrl.split("https://")
        fixedPhotoUrl = "https://" + parts[parts.length - 1]
      }

      setLocalFormData((prev) => ({
        ...prev,
        patient_photo: fixedPhotoUrl,
        patient_photo_preview: fixedPhotoUrl,
        first_name: formData.first_name || formData.name?.split(" ")[0] || "",
        middle_name: formData.middle_name || formData.name?.split(" ")[1] || "",
        last_name: formData.last_name || formData.name?.split(" ").slice(2).join(" ") || "",
        gender: formData.gender || "",
        dob: formData.dob || formData.date_of_birth || "",
        weight: formData.weight || "",
        occupation: formData.occupation || "",
        maritalStatus: maritalStatus,
        marriageEntries: marriageEntries,
        disease_ids: diseaseIds,
        current_disease_id: initialCurrentDiseaseId || "",
        disease_status: diseaseStatus,
        disease_percentage: initialDiseasePercentage,
        intensity_of_treatment: formData.intensity_of_treatment || "",
        referred_by_patient_id: initialReferredBy,
        referred_by_manual: formData.referred_by_manual || "",
        spouse_name: formData.spouseName || "",
      }))

      setSelectedIntensity(initialIntensity)
    }
  }, [isOpen, formData, diseases])

  useEffect(() => {
    return () => {
      if (localFormData.patient_photo_preview && typeof localFormData.patient_photo_preview === "string" && localFormData.patient_photo_preview.startsWith("blob:")) {
        URL.revokeObjectURL(localFormData.patient_photo_preview)
      }
    }
  }, [localFormData.patient_photo_preview])

  const handleChange = (e) => {
    const { name, value } = e.target
    setLocalFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleGenderChange = (selectedOption) => {
    const gender = selectedOption.value
    setLocalFormData((prev) => ({
      ...prev,
      gender: gender,
    }))
    dispatch(getPatientlistbygenderAction(gender))
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = ["image/jpeg", "image/png"]
      if (!validTypes.includes(file.type)) {
        showErrorAlert("Please select a JPEG or PNG image.")
        return
      }
      setLocalFormData((prev) => ({
        ...prev,
        patient_photo: file,
        patient_photo_preview: URL.createObjectURL(file),
      }))
    }
  }

  const handleMarriageEntryChange = (index, e) => {
    const { name, value } = e.target
    const errorKey = `${name}_${index}`
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }
    setLocalFormData((prev) => ({
      ...prev,
      marriageEntries: prev.marriageEntries.map((entry, i) => (i === index ? { ...entry, [name]: value } : entry)),
    }))
  }

  const handleSelectChange = (selectedOption, index) => {
    if (errors[`spouseDetails_${index}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[`spouseDetails_${index}`]
        return newErrors
      })
    }
    setLocalFormData((prevFormData) => {
      const updatedEntries = [...prevFormData.marriageEntries]
      updatedEntries[index] = {
        ...updatedEntries[index],
        spouseDetails: selectedOption ? selectedOption.value : null,
      }
      return {
        ...prevFormData,
        marriageEntries: updatedEntries,
      }
    })
  }

  const addMarriageEntry = () => {
    const newIndex = localFormData.marriageEntries.length
    const newEntry = {
      marriageHistory: `${newIndex + 1}${getOrdinalSuffix(newIndex + 1)}`,
      marriageDate: "",
      spouseOption: "",
      spouseDetails: null,
      spouseName: "",
      spouseMobile: "",
      spouseEmail: "",
      spouseDOB: "",
      spouseGender: "",
      spouseOccupation: "",
    }
    setLocalFormData((prev) => ({
      ...prev,
      marriageEntries: [...prev.marriageEntries, newEntry],
    }))
  }

  const deleteMarriageEntry = (index) => {
    if (localFormData.marriageEntries.length <= 1) return
    const updatedEntries = localFormData.marriageEntries.filter((_, i) => i !== index)
    const reOrdered = updatedEntries.map((entry, i) => ({
      ...entry,
      marriageHistory: `${i + 1}${getOrdinalSuffix(i + 1)}`,
    }))
    setLocalFormData((prev) => ({
      ...prev,
      marriageEntries: reOrdered,
    }))
  }

  const getOrdinalSuffix = (n) => {
    const s = ["th", "st", "nd", "rd"],
      v = n % 100
    return s[(v - 20) % 10] || s[v] || s[0]
  }

  const calculateMarriageAge = (marriageDate) => {
    if (!marriageDate) return ""
    const today = new Date()
    const marriage = new Date(marriageDate)
    if (isNaN(marriage.getTime())) return ""

    let years = today.getFullYear() - marriage.getFullYear()
    let months = today.getMonth() - marriage.getMonth()
    let days = today.getDate() - marriage.getDate()

    if (days < 0) {
      months--
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0)
      days += prevMonth.getDate()
    }

    if (months < 0) {
      years--
      months += 12
    }

    const weeks = Math.floor(days / 7)
    const parts = []
    if (years > 0) parts.push(`${years} y${years > 1 ? "s" : ""}`)
    if (months > 0) parts.push(`${months} m${months > 1 ? "s" : ""}`)
    if (weeks > 0) parts.push(`${weeks} w${weeks > 1 ? "s" : ""}`)
    return parts.join(", ") || "0 weeks"
  }

  const formatMarriageEntriesForAPI = (entries) => {
    return entries.map((entry) => {
      const withSpouse = entry.spouseOption === "withSpouse"
      return {
        marriageNumber: entry.marriageHistory,
        dateOfMarriage: entry.marriageDate,
        withSpouse: withSpouse.toString(),
        spouseName: withSpouse ? "" : entry.spouseName,
        marriageAge: calculateMarriageAge(entry.marriageDate).toString(),
        spouse_name: withSpouse ? "" : entry.spouseName,
        spousedetails: withSpouse ? entry.spouseDetails : "",
        spouse_mobile: withSpouse ? entry.spouseMobile ?? "" : "",
        spouse_email: withSpouse ? entry.spouseEmail ?? "" : "",
        spouse_dob: withSpouse ? entry.spouseDOB ?? "" : "",
        spouse_gender: withSpouse ? entry.spouseGender ?? "" : "",
        spouse_occupation: withSpouse ? entry.spouseOccupation ?? "" : "",
      }
    })
  }

  const handleDiseasesChange = (selectedOptions) => {
    const diseaseIds = selectedOptions ? selectedOptions.map((opt) => String(opt.value)) : []
    setLocalFormData((prev) => ({
      ...prev,
      disease_ids: diseaseIds,
    }))
  }

  const handleCurrentDiseasesChange = (selectedOption) => {
    setLocalFormData((prev) => ({
      ...prev,
      current_disease_id: selectedOption ? selectedOption.value : "",
    }))
  }

  // Family Tree Handlers
  const handleAddFamilyMember = async () => {
    const patientId = formData.patient_id
    if (!patientId) {
      showErrorAlert("Patient ID is missing. Cannot add family member.")
      return
    }
    if (!familyFormData.relation_name || !familyFormData.family_patient_id) {
      showErrorAlert("Please select both a relation and a patient to add a family member.")
      return
    }

    setIsProcessingFamily(true)
    try {
      await dispatch(
        addFamilyTreeAction(patientId, familyFormData.family_patient_id.value, familyFormData.relation_name.value),
      )

      setFamilyFormData({
        relation_name: null,
        family_patient_id: null,
      })

      await dispatch(fetchFamilyTreeListAction(patientId))
      handleSuccess("Family member added successfully!")
    } catch (error) {
      console.error("Error adding family member:", error)
      showErrorAlert("Failed to add family member. Please try again.")
    } finally {
      setIsProcessingFamily(false)
    }
  }

  const handleEditFamilyMember = async () => {
    const patientId = formData.patient_id
    if (!patientId || !editingFamilyId) {
      showErrorAlert("Missing required information for editing.")
      return
    }
    if (!familyFormData.relation_name || !familyFormData.family_patient_id) {
      showErrorAlert("Please select both a relation and a patient.")
      return
    }

    setIsProcessingFamily(true)
    try {
      await dispatch(
        editFamilyTreeAction(
          patientId,
          familyFormData.family_patient_id.value,
          familyFormData.relation_name.value,
          editingFamilyId,
        ),
      )

      setFamilyFormData({
        relation_name: null,
        family_patient_id: null,
      })
      setEditingFamilyId(null)

      await dispatch(fetchFamilyTreeListAction(patientId))
      handleSuccess("Family member updated successfully!")
    } catch (error) {
      console.error("Error editing family member:", error)
      showErrorAlert("Failed to edit family member. Please try again.")
    } finally {
      setIsProcessingFamily(false)
    }
  }

  const handleDeleteFamilyMember = async (familyId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the family member.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    setIsProcessingFamily(true)
    try {
      await dispatch(deleteFamilyTreeAction(familyId))

      await Swal.fire({
        title: "Deleted!",
        text: "The family member has been deleted.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })

      await dispatch(fetchFamilyTreeListAction(formData.patient_id))
    } catch (error) {
      console.error("Error deleting family member:", error)
      Swal.fire({
        title: "Error",
        text: "Failed to delete family member. Please try again.",
        icon: "error",
      })
    } finally {
      setIsProcessingFamily(false)
    }
  }

  const handleStartEdit = (member) => {
    // Find the patient in the patients list to get complete details
    const selectedPatient = patients.find(p => p.patient_id === member.family_patient_id)
    
    // Create the selected option with proper label
    const selectedOption = selectedPatient ? {
      value: selectedPatient.patient_id,
      label: formatPatientName(selectedPatient),
      first_name: selectedPatient.first_name,
      middile_name: selectedPatient.middile_name,
      last_name: selectedPatient.last_name,
    } : {
      value: member.family_patient_id,
      label: member.formattedName || "Unknown Patient",
    }
    
    setEditingFamilyId(member.family_id)
    setFamilyFormData({
      relation_name: relationOptions.find((opt) => opt.value === member.relation_name) || null,
      family_patient_id: selectedOption,
    })
  }

  const handleCancelEdit = () => {
    setEditingFamilyId(null)
    setFamilyFormData({
      relation_name: null,
      family_patient_id: null,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    const newErrors = {}
    
    if (!localFormData.first_name) newErrors.first_name = "First name is required"
    if (!localFormData.gender) newErrors.gender = "Gender is required"
    if (!localFormData.occupation) newErrors.occupation = "Occupation is required"
    
    if (localFormData.maritalStatus === "Yes") {
      localFormData.marriageEntries.forEach((entry, index) => {
        if (!entry.marriageDate?.trim()) {
          newErrors[`marriageDate_${index}`] = "Please enter the marriage date."
        }
        if (!entry.spouseOption) {
          newErrors[`spouseOption_${index}`] = "Please select spouse option."
        }
        if (entry.spouseOption === "withSpouse") {
          if (!entry.spouseDetails) {
            newErrors[`spouseDetails_${index}`] = "Please select spouse details."
          }
        }
      })
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setIsSubmitting(false)
      const firstErrorField = Object.keys(newErrors)[0]
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`)
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      return
    }
    
    try {
      const patientId = formData.patient_id
      if (!patientId) {
        throw new Error("Patient ID is missing")
      }
      
      const diseaseIdString = localFormData.disease_ids.join("|")
      
      const patientUpdateData = {
        patient_id: patientId,
        first_name: localFormData.first_name,
        middle_name: localFormData.middle_name,
        last_name: localFormData.last_name,
        whatsapp_contact_no: formData.Contact || "",
        patient_email: formData.email || "",
        patient_photo: localFormData.patient_photo,
        branch: formData.branch || "",
        additional_contact_no: formData.additionalContact || "",
        date_of_birth: localFormData.dob,
        height: formData.height || "",
        weight: localFormData.weight || "",
        occupation: localFormData.occupation,
        id_proof: formData.idProof || "",
        id_number: formData.idNumber || "",
        gender: localFormData.gender,
        age: formData.age || "",
        disease_names: diseaseIdString,
        current_disease_id: localFormData.current_disease_id,
        disease_status: localFormData.disease_status,
        disease_percentage: localFormData.disease_percentage,
        intensity_of_treatment: localFormData.intensity_of_treatment,
        referred_by: localFormData.referred_by_patient_id || "",
        referred_by_manual: localFormData.referred_by_manual || "",
      }
      
      await dispatch(updatePatientAction(patientUpdateData))
      
      const formattedMarriageData = formatMarriageEntriesForAPI(localFormData.marriageEntries)
      await dispatch(
        addMarriageInfoAction({
          patient_id: patientId,
          is_married: localFormData.maritalStatus,
          marriage_entries: localFormData.maritalStatus === "Yes" ? formattedMarriageData : null,
        }),
      )
      
      handleSuccess("Personal information updated successfully!")
      
      if (onSubmit && typeof onSubmit === "function") {
        onSubmit()
      }
      if (onClose && typeof onClose === "function") {
        onClose()
      }
    } catch (error) {
      console.error("Error updating personal info:", error)
      showErrorAlert(error.message || "Failed to update personal information")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Transgender", label: "Transgender" },
  ]

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Personal Information</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                {/* Profile Picture */}
                <div className="col-12 mb-4">
                  <label className="form-label">Profile Picture</label>
                  <div className="input-group">
                    <input
                      type="file"
                      id="patientPhotoInput"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                      disabled={isSubmitting}
                    />
                    <label htmlFor="patientPhotoInput" className="btn btn-outline-primary">
                      Choose File
                    </label>
                    <div style={{ position: "relative", width: "80%" }}>
                      <input
                        type="text"
                        className="form-control pe-4"
                        readOnly
                        value={
                          localFormData.patient_photo
                            ? typeof localFormData.patient_photo === "string"
                              ? localFormData.patient_photo.split("/").pop()
                              : localFormData.patient_photo.name
                            : ""
                        }
                      />
                      {localFormData.patient_photo && (
                        <span
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontWeight: "bold",
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                          title="Clear"
                          onClick={() =>
                            setLocalFormData((prev) => ({
                              ...prev,
                              patient_photo: "",
                              patient_photo_preview: "",
                            }))
                          }
                        >
                          ×
                        </span>
                      )}
                    </div>
                  </div>
                  {localFormData.patient_photo_preview && (
                    <div className="mt-3 text-center">
                      <img
                        src={getValidPhotoUrl(localFormData.patient_photo_preview)}
                        alt="Preview"
                        style={{
                          maxHeight: "150px",
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = "/placeholder.svg"
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Name Fields */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
                    name="first_name"
                    value={localFormData.first_name}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    disabled={isSubmitting}
                  />
                  {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Middle Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="middle_name"
                    value={localFormData.middle_name}
                    onChange={handleChange}
                    placeholder="Enter middle name"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="last_name"
                    value={localFormData.last_name}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Gender */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Gender <span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    name="gender"
                    options={genderOptions}
                    value={localFormData.gender ? { value: localFormData.gender, label: localFormData.gender } : null}
                    onChange={handleGenderChange}
                    placeholder="Select Gender"
                    isDisabled={isSubmitting}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                    }}
                  />
                  {errors.gender && <div className="text-danger mt-1">{errors.gender}</div>}
                </div>

                {/* Date of Birth */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Date of Birth</label>
                  <div className="position-relative">
                    <input
                      type="date"
                      className="form-control"
                      name="dob"
                      value={localFormData.dob}
                      onChange={handleChange}
                      max={new Date().toISOString().split("T")[0]}
                      disabled={isSubmitting}
                    />
                    <i
                      className="fa fa-calendar position-absolute text-muted"
                      style={{
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        fontSize: "18px",
                      }}
                    ></i>
                  </div>
                </div>

                {/* Weight */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Weight (kg)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="weight"
                    value={localFormData.weight}
                    onChange={handleChange}
                    placeholder="Enter weight"
                    min="0"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Occupation */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Occupation <span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    name="occupation"
                    options={occupationOptions}
                    value={
                      localFormData.occupation
                        ? occupationOptions.find((opt) => opt.value === localFormData.occupation)
                        : null
                    }
                    onChange={(selectedOption) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        occupation: selectedOption ? selectedOption.value : "",
                      }))
                    }
                    placeholder="Select Occupation"
                    isClearable
                    isDisabled={isSubmitting}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                      }),
                    }}
                  />
                  {errors.occupation && <div className="text-danger mt-1">{errors.occupation}</div>}
                </div>

                {/* Diseases */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Diseases</label>
                  <Select
                    isMulti
                    options={diseases.map((d) => ({
                      value: d.disease_id,
                      label: d.disease_name,
                    }))}
                    value={
                      localFormData.disease_ids?.length > 0
                        ? diseases
                            .filter((d) => localFormData.disease_ids.includes(String(d.disease_id)))
                            .map((d) => ({
                              value: d.disease_id,
                              label: d.disease_name,
                            }))
                        : []
                    }
                    onChange={handleDiseasesChange}
                    placeholder="Select Diseases"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                      }),
                    }}
                  />
                </div>

                {/* Disease Percentage */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Disease Percentage (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="disease_percentage"
                    value={localFormData.disease_percentage}
                    onChange={(e) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        disease_percentage: Math.max(0, Math.min(100, e.target.value)),
                      }))
                    }
                    min="0"
                    max="100"
                    placeholder="Enter percentage"
                  />
                </div>

                {/* Current Diseases */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Current Diseases</label>
                  <Select
                    options={diseases
                      .filter((d) => localFormData.disease_ids.includes(String(d.disease_id)))
                      .map((d) => ({
                        value: d.disease_id,
                        label: d.disease_name,
                      }))}
                    value={
                      localFormData.current_disease_id
                        ? diseases
                            .map((d) => ({
                              value: d.disease_id,
                              label: d.disease_name,
                            }))
                            .find((opt) => String(opt.value) === String(localFormData.current_disease_id))
                        : null
                    }
                    onChange={handleCurrentDiseasesChange}
                    placeholder="Select Current Disease"
                    isClearable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                      }),
                    }}
                  />
                </div>

                {/* Disease Status */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Disease Status</label>
                  <Select
                    name="disease_status"
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Resolved", label: "Resolved" },
                      { value: "Remission", label: "Remission" },
                      { value: "Chronic", label: "Chronic" },
                      { value: "Recovered", label: "Recovered" },
                      { value: "Progressing", label: "Progressing" },
                      { value: "Stable", label: "Stable" },
                      { value: "Terminal", label: "Terminal" },
                    ]}
                    value={
                      localFormData.disease_status
                        ? { value: localFormData.disease_status, label: localFormData.disease_status }
                        : null
                    }
                    onChange={(selectedOption) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        disease_status: selectedOption ? selectedOption.value : "",
                      }))
                    }
                    placeholder="Select Disease Status"
                    isDisabled={isSubmitting}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                    }}
                    isClearable
                  />
                </div>

                {/* Intensity of Treatment */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Intensity of Treatment</label>
                  <Select
                    options={intensityOptions}
                    value={selectedIntensity}
                    onChange={(selected) => {
                      setSelectedIntensity(selected)
                      setLocalFormData((prev) => ({
                        ...prev,
                        intensity_of_treatment: selected ? selected.value : "",
                      }))
                    }}
                    placeholder="Select intensity..."
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "50px",
                      }),
                    }}
                  />
                </div>

                {/* Married Checkbox */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label fw-bold">Married</label>
                  <div className="form-check d-flex align-items-center" style={{ height: "40px" }}>
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      id="maritalStatusCheckbox"
                      checked={localFormData.maritalStatus === "Yes"}
                      onChange={(e) =>
                        setLocalFormData((prev) => ({
                          ...prev,
                          maritalStatus: e.target.checked ? "Yes" : "No",
                        }))
                      }
                      disabled={isSubmitting}
                    />
                    <label className="form-check-label m-0" htmlFor="maritalStatusCheckbox" style={{ cursor: "pointer" }}>
                      Yes
                    </label>
                  </div>
                </div>
              </div>

              {/* Marriage Entries Section */}
              {localFormData.maritalStatus === "Yes" && (
                <div className="marriage-section">
                  <h6 className="mb-3">Marriage Information</h6>
                  {localFormData.marriageEntries.map((entry, index) => (
                    <div
                      key={index}
                      className="marriage-entry mb-4 p-3"
                      style={{
                        border: "1px solid #eee",
                        borderRadius: "8px",
                        background: "#f9f9f9",
                      }}
                    >
                      <div className="row align-items-center mb-3">
                        <div className="col-10">
                          <h6 className="mb-0">Marriage Entry #{index + 1}</h6>
                        </div>
                        <div className="col-2 text-end">
                          {localFormData.marriageEntries.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteMarriageEntry(index)}
                              disabled={isSubmitting}
                            >
                              <i className="fa fa-trash"></i> Delete
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        {/* Marriage History */}
                        <div className="col-xl-4 mb-3">
                          <label className="form-label fw-bold">
                            Marriage History <span style={{ color: "red" }}>*</span>
                          </label>
                          <select
                            className={`form-select ${errors[`marriageHistory_${index}`] ? "is-invalid" : ""}`}
                            name="marriageHistory"
                            value={entry.marriageHistory}
                            onChange={(e) => handleMarriageEntryChange(index, e)}
                            style={{ height: "50px" }}
                            disabled={isSubmitting}
                          >
                            <option value="1st">1st</option>
                            <option value="2nd">2nd</option>
                            <option value="3rd">3rd</option>
                            <option value="4th">4th</option>
                          </select>
                        </div>

                        {/* Date of Marriage */}
                        <div className="col-xl-4 mb-3">
                          <label className="form-label fw-bold">
                            Date of Marriage <span style={{ color: "red" }}>*</span>
                          </label>
                          <div className="position-relative">
                            <input
                              type="date"
                              className={`form-control ${errors[`marriageDate_${index}`] ? "is-invalid" : ""}`}
                              name="marriageDate"
                              value={entry.marriageDate || ""}
                              onChange={(e) => handleMarriageEntryChange(index, e)}
                              style={{ height: "50px" }}
                              disabled={isSubmitting}
                              max={new Date().toISOString().split("T")[0]}
                            />
                            <i
                              className="fa fa-calendar position-absolute text-muted"
                              style={{
                                right: "15px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                fontSize: "18px",
                              }}
                            ></i>
                          </div>
                          {errors[`marriageDate_${index}`] && (
                            <div className="text-danger mt-1">{errors[`marriageDate_${index}`]}</div>
                          )}
                        </div>

                        {/* Marriage Age */}
                        {entry.marriageDate && (
                          <div className="col-xl-4 mb-3">
                            <label className="form-label fw-bold">Marriage Age</label>
                            <input
                              type="text"
                              className="form-control"
                              value={calculateMarriageAge(entry.marriageDate)}
                              readOnly
                              style={{
                                height: "50px",
                                backgroundColor: "#f8f9fa",
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Spouse Option */}
                      <div className="row">
                        <div className="col-xl-12 mb-3">
                          <label className="form-label fw-bold">
                            With Spouse <span style={{ color: "red" }}>*</span>
                          </label>
                          <div className="d-flex align-items-center gap-4 mt-2">
                            <div className="form-check d-flex align-items-center">
                              <input
                                type="radio"
                                className="form-check-input me-2"
                                id={`withSpouse-${index}`}
                                name={`spouseOption-${index}`}
                                value="withSpouse"
                                checked={entry.spouseOption === "withSpouse"}
                                onChange={() =>
                                  handleMarriageEntryChange(index, {
                                    target: { name: "spouseOption", value: "withSpouse" },
                                  })
                                }
                                disabled={isSubmitting}
                              />
                              <label className="form-check-label mb-0" htmlFor={`withSpouse-${index}`}>
                                Yes
                              </label>
                            </div>

                            <div className="form-check d-flex align-items-center">
                              <input
                                type="radio"
                                className="form-check-input me-2"
                                id={`withoutSpouse-${index}`}
                                name={`spouseOption-${index}`}
                                value="withoutSpouse"
                                checked={entry.spouseOption === "withoutSpouse"}
                                onChange={() =>
                                  handleMarriageEntryChange(index, {
                                    target: { name: "spouseOption", value: "withoutSpouse" },
                                  })
                                }
                                disabled={isSubmitting}
                              />
                              <label className="form-check-label mb-0" htmlFor={`withoutSpouse-${index}`}>
                                No
                              </label>
                            </div>
                          </div>
                          {errors[`spouseOption_${index}`] && (
                            <div className="text-danger mt-1">{errors[`spouseOption_${index}`]}</div>
                          )}
                        </div>

                        {/* With Spouse Fields */}
                        {entry.spouseOption === "withSpouse" && (
                          <>
                            <div className="col-xl-4 mb-3">
                              <label className="form-label fw-bold">
                                Spouse Details <span style={{ color: "red" }}>*</span>
                              </label>
                              <Select
                                name={`spouseDetails-${index}`}
                                options={patientOptions}
                                value={
                                  entry.spouseDetails
                                    ? patientOptions.find((opt) => opt.value === entry.spouseDetails)
                                    : null
                                }
                                onChange={(selectedOption) => handleSelectChange(selectedOption, index)}
                                placeholder="Select Spouse from Patients"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    minHeight: "50px",
                                    height: "50px",
                                  }),
                                }}
                              />
                              {errors[`spouseDetails_${index}`] && (
                                <div className="text-danger mt-1">{errors[`spouseDetails_${index}`]}</div>
                              )}
                            </div>

                            <div className="col-xl-4 mb-3">
                              <label className="form-label fw-bold">Spouse Mobile</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Mobile Number"
                                name="spouseMobile"
                                value={entry.spouseMobile || ""}
                                onChange={(e) => handleMarriageEntryChange(index, e)}
                                maxLength="10"
                                style={{ height: "50px" }}
                                disabled={isSubmitting}
                              />
                            </div>

                            <div className="col-xl-4 mb-3">
                              <label className="form-label fw-bold">Spouse Email</label>
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Enter Email"
                                name="spouseEmail"
                                value={entry.spouseEmail || ""}
                                onChange={(e) => handleMarriageEntryChange(index, e)}
                                style={{ height: "50px" }}
                                disabled={isSubmitting}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* With Spouse Additional Fields */}
                      {entry.spouseOption === "withSpouse" && (
                        <div className="row">
                          <div className="col-xl-4 mb-3 position-relative">
                            <label className="form-label fw-bold">Spouse Date of Birth</label>
                            <div className="position-relative">
                              <input
                                type="date"
                                className="form-control"
                                name="spouseDOB"
                                value={entry.spouseDOB || ""}
                                onChange={(e) => handleMarriageEntryChange(index, e)}
                                max={new Date().toISOString().split("T")[0]}
                                style={{ height: "50px" }}
                                disabled={isSubmitting}
                              />
                              <i
                                className="fa fa-calendar position-absolute text-muted"
                                style={{
                                  right: "15px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                  fontSize: "18px",
                                }}
                              ></i>
                            </div>
                          </div>

                          <div className="col-xl-4 mb-3">
                            <label className="form-label fw-bold">Spouse Gender</label>
                            <Select
                              name="spouseGender"
                              value={genderOptions.find((opt) => opt.value === entry.spouseGender) || null}
                              onChange={(selectedOption) => {
                                handleMarriageEntryChange(index, {
                                  target: {
                                    name: "spouseGender",
                                    value: selectedOption ? selectedOption.value : "",
                                  },
                                })
                              }}
                              options={genderOptions}
                              placeholder="Select Gender"
                              isDisabled={isSubmitting}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  minHeight: "50px",
                                }),
                              }}
                            />
                          </div>

                          <div className="col-xl-4 mb-3">
                            <label className="form-label fw-bold">Spouse Occupation</label>
                            <Select
                              name="spouseOccupation"
                              options={occupationOptions}
                              value={
                                entry.spouseOccupation
                                  ? occupationOptions.find((opt) => opt.value === entry.spouseOccupation)
                                  : null
                              }
                              onChange={(selectedOption) => {
                                handleMarriageEntryChange(index, {
                                  target: {
                                    name: "spouseOccupation",
                                    value: selectedOption ? selectedOption.value : "",
                                  },
                                })
                              }}
                              placeholder="Select Occupation"
                              isClearable
                              isDisabled={isSubmitting}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  minHeight: "50px",
                                }),
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Without Spouse Fields */}
                      {entry.spouseOption === "withoutSpouse" && (
                        <div className="row mt-3">
                          <div className="col-xl-4 mb-3">
                            <label className="form-label fw-bold">Spouse Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Spouse Name"
                              name="spouseName"
                              value={entry.spouseName || ""}
                              onChange={(e) => handleMarriageEntryChange(index, e)}
                              style={{ height: "50px" }}
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="row mt-3 mb-4">
                    <div className="col-2">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={addMarriageEntry}
                        disabled={isSubmitting}
                      >
                        <i className="fa fa-plus me-2"></i> Add Marriage
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Family Tree Section */}
              <div className="row mt-4">
                <div className="col-12">
                  <h6 className="mb-3">Family Tree</h6>
                  
                  {/* Add/Edit Family Member Form */}
                  <div className="row mb-4">
                    <div className="col-md-5 mb-2">
                      <Select
                        options={relationOptions}
                        value={familyFormData.relation_name}
                        onChange={(selected) =>
                          setFamilyFormData((prev) => ({ ...prev, relation_name: selected }))
                        }
                        placeholder="Select Relation"
                        isDisabled={isProcessingFamily}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: "45px",
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-5 mb-2">
                      <Select
                        options={patientOptions}
                        value={familyFormData.family_patient_id}
                        onChange={(selected) =>
                          setFamilyFormData((prev) => ({ ...prev, family_patient_id: selected }))
                        }
                        placeholder="Select Patient"
                        isDisabled={isProcessingFamily}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: "45px",
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      {editingFamilyId ? (
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-success btn-sm w-100"
                            onClick={handleEditFamilyMember}
                            disabled={isProcessingFamily}
                          >
                            {isProcessingFamily ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                              "Update"
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={handleCancelEdit}
                            disabled={isProcessingFamily}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm w-100"
                          onClick={handleAddFamilyMember}
                          disabled={isProcessingFamily}
                        >
                          {isProcessingFamily ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            "Add Member"
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Family Members List */}
                  {familyMembers.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Relation</th>
                            <th>Patient Name</th>
                            <th style={{ width: "100px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {familyMembers.map((member) => (
                            <tr key={member.family_id}>
                              <td>{member.relation_name}</td>
                              <td>{member.formattedName || "N/A"}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-info"
                                    onClick={() => handleStartEdit(member)}
                                    disabled={isProcessingFamily}
                                    title="Edit"
                                  >
                                    <i className="fa fa-edit"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteFamilyMember(member.family_id)}
                                    disabled={isProcessingFamily}
                                    title="Delete"
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {familyMembers.length === 0 && (
                    <div className="text-center text-muted py-3">
                      <p className="mb-0">No family members added yet.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Referred By Section */}
              <div className="row mt-4 align-items-center">
                <div className="col-md-5 mb-3">
                  <label className="form-label fw-bold">Referred By</label>
                  <Select
                    options={patients.map((p) => ({
                      value: p.patient_id,
                      label: formatPatientName(p),
                    }))}
                    placeholder="Select Patient"
                    isClearable
                    value={
                      localFormData.referred_by_patient_id
                        ? {
                            value: localFormData.referred_by_patient_id,
                            label: formatPatientName(
                              patients.find((p) => p.patient_id === localFormData.referred_by_patient_id)
                            ),
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        referred_by_patient_id: selectedOption ? selectedOption.value : "",
                        referred_by_manual: "",
                      }))
                    }
                    isDisabled={isSubmitting || localFormData.referred_by_manual}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                      }),
                    }}
                  />
                </div>

                <div className="col-md-2 text-center mb-3">
                  <div style={{ fontWeight: "600", fontSize: "16px" }}>OR</div>
                </div>

                <div className="col-md-5 mb-3">
                  <label className="form-label fw-bold">Referred By Other</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter name"
                    value={localFormData.referred_by_manual}
                    onChange={(e) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        referred_by_manual: e.target.value,
                        referred_by_patient_id: "",
                      }))
                    }
                    disabled={isSubmitting || localFormData.referred_by_patient_id}
                    style={{ height: "50px" }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-danger" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span> Updating...
                  </>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default PersonalInfoModal